/**
 * Endpoint path builders for the backend REST API (ARCHITECTURE §2, `api/v1/`).
 *
 * Centralizes route strings so resources never hand-concatenate paths. All paths
 * are relative to the API base URL configured on the {@link HttpClient}; the
 * `/api/v1` prefix is included here.
 */

const V1 = '/api/v1';

/** Encode a path segment (ids may contain characters needing escaping). */
const seg = (value: string): string => encodeURIComponent(value);

export const endpoints = {
  /** Liveness/readiness (mounted at root, no version prefix). */
  health: () => `/health`,
  ready: () => `/ready`,

  /** Owner/admin auth — app-managed email + password (no external IdP). */
  authOwner: {
    register: () => `${V1}/auth/owner/register`,
    login: () => `${V1}/auth/owner/login`,
    refresh: () => `${V1}/auth/owner/refresh`,
    me: () => `${V1}/auth/owner/me`,
  },

  /** App-managed agent auth. */
  authAgent: {
    login: () => `${V1}/auth/agent/login`,
    refresh: () => `${V1}/auth/agent/refresh`,
    acceptInvite: () => `${V1}/auth/agent/accept-invite`,
    forgot: () => `${V1}/auth/agent/forgot`,
    reset: () => `${V1}/auth/agent/reset`,
    me: () => `${V1}/auth/agent/me`,
    status: () => `${V1}/auth/agent/status`,
  },

  /** Public (unauthenticated) org lookup by workspace slug — branded login + not-found gate. */
  publicOrgs: {
    bySlug: (slug: string) => `${V1}/public/orgs/${seg(slug)}`,
  },

  organizations: {
    current: () => `${V1}/organizations/current`,
    widgetConfig: () => `${V1}/organizations/current/widget-config`,
    botConfig: () => `${V1}/organizations/current/bot-config`,
    notificationConfig: () => `${V1}/organizations/current/notification-config`,
    translationConfig: () => `${V1}/organizations/current/translation-config`,
    domain: () => `${V1}/organizations/current/domain`,
    domainVerify: () => `${V1}/organizations/current/domain/verify`,
  },
  voice: {
    config: () => `${V1}/voice/config`,
    calls: () => `${V1}/voice/calls`,
    /** Non-admin readiness check (whether call controls should show). */
    available: () => `${V1}/voice/available`,
    /** Mint a browser voice access token for the current agent's softphone. */
    token: () => `${V1}/voice/token`,
    /** The org's Twilio number pool (per-agent assignment). */
    numbers: () => `${V1}/voice/numbers`,
    number: (id: string) => `${V1}/voice/numbers/${seg(id)}`,
  },

  /** Web Push — VAPID key + browser subscription register/drop (agent desktop notifications). */
  push: {
    vapidKey: () => `${V1}/push/vapid-public-key`,
    subscriptions: () => `${V1}/push/subscriptions`,
  },

  /** Agent-WhatsApp relay — link/verify the agent's own WhatsApp number. */
  whatsappRelay: {
    links: () => `${V1}/whatsapp-relay/links`,
    link: () => `${V1}/whatsapp-relay/link`,
    verify: () => `${V1}/whatsapp-relay/verify`,
    unlink: (id: string) => `${V1}/whatsapp-relay/links/${seg(id)}`,
    adminTeam: () => `${V1}/whatsapp-relay/admin/team`,
    adminSetLink: (agentId: string) => `${V1}/whatsapp-relay/admin/team/${seg(agentId)}`,
  },


  /** Staff-to-staff DMs (the Online Agents panel). */
  agentChat: {
    unread: () => `${V1}/agent-chat/unread`,
    messages: (peerId: string) => `${V1}/agent-chat/${seg(peerId)}/messages`,
  },

  /** Live visitors currently browsing with the widget loaded (cockpit Visitors panel). */
  visitors: {
    active: () => `${V1}/visitors/active`,
    /** Agent-initiated chat: create a session + send the first message to a browsing visitor. */
    engage: (visitorId: string) => `${V1}/visitors/${seg(visitorId)}/engage`,
    /** The visitor's page-by-page journey (old "View visitor path"). */
    path: (visitorId: string) => `${V1}/visitors/${seg(visitorId)}/path`,
  },

  /** Chat-translation proxy (Google key stays server-side). */
  translate: () => `${V1}/translate`,
  translateAvailable: () => `${V1}/translate/available`,

  /** Smart Compose — inline composer completion. */
  compose: {
    suggest: () => `${V1}/compose/suggest`,
  },

  /** Agent dialer — outbound WhatsApp/SMS to an arbitrary number + capability probe. */
  outbound: {
    availability: () => `${V1}/outbound/availability`,
    message: () => `${V1}/outbound/message`,
    waTemplates: () => `${V1}/outbound/wa-templates`,
    metaWaTemplates: () => `${V1}/outbound/wa-templates/meta`,
  },

  /** Billing / plans. */
  billing: {
    plans: () => `${V1}/billing/plans`,
    status: () => `${V1}/billing/status`,
    checkout: () => `${V1}/billing/checkout`,
    cancel: () => `${V1}/billing/cancel`,
  },


  /** Custom roles (named permission bundles) + the permission catalog. */
  roles: {
    list: () => `${V1}/roles`,
    create: () => `${V1}/roles`,
    detail: (roleId: string) => `${V1}/roles/${seg(roleId)}`,
    permissions: () => `${V1}/roles/permissions`,
  },

  agents: {
    list: (includeInactive?: boolean) =>
      `${V1}/agents${includeInactive ? '?includeInactive=true' : ''}`,
    create: () => `${V1}/agents`,
    detail: (agentId: string) => `${V1}/agents/${seg(agentId)}`,
    invite: (agentId: string) => `${V1}/agents/${seg(agentId)}/invite`,
    reactivate: (agentId: string) => `${V1}/agents/${seg(agentId)}/reactivate`,
    /** Current agent's own notification preferences (GET/PATCH). */
    notificationPrefs: () => `${V1}/agents/me/notification-prefs`,
  },

  customers: {
    list: () => `${V1}/customers`,
    detail: (customerId: string) => `${V1}/customers/${seg(customerId)}`,
    /** A customer's past conversations (cockpit past-chats panel). */
    chats: (customerId: string) => `${V1}/customers/${seg(customerId)}/chats`,
  },

  chats: {
    list: () => `${V1}/chats`,
    detail: (sessionId: string) => `${V1}/chats/${seg(sessionId)}`,
    messages: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/messages`,
    claim: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/claim`,
    assign: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/assign`,
    takeover: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/takeover`,
    participants: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/participants`,
    close: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/close`,
    read: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/read`,
    transcript: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/transcript`,
    /** Email the transcript to the visitor (or an explicit recipient). */
    emailTranscript: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/transcript/email`,
    cobrowseStart: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/cobrowse/start`,
    cobrowseStop: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/cobrowse/stop`,
    cobrowseFrame: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/cobrowse/frame`,
    tags: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/tags`,
    notes: (sessionId: string) => `${V1}/chats/${seg(sessionId)}/notes`,
    note: (sessionId: string, noteId: string) =>
      `${V1}/chats/${seg(sessionId)}/notes/${seg(noteId)}`,
  },

  /** Saved/canned replies (agent productivity). */
  cannedResponses: {
    list: () => `${V1}/canned-responses`,
    create: () => `${V1}/canned-responses`,
    detail: (id: string) => `${V1}/canned-responses/${seg(id)}`,
  },

  /** Support tickets (raised from a conversation or standalone). */
  tickets: {
    list: () => `${V1}/tickets`,
    create: () => `${V1}/tickets`,
    detail: (id: string) => `${V1}/tickets/${seg(id)}`,
  },

  /** Teams / departments. */
  departments: {
    list: () => `${V1}/departments`,
    create: () => `${V1}/departments`,
    detail: (id: string) => `${V1}/departments/${seg(id)}`,
  },

  /** Banned/blocked visitors (moderation). */
  moderation: {
    block: (customerId: string) => `${V1}/moderation/customers/${seg(customerId)}/block`,
    unblock: (customerId: string) => `${V1}/moderation/customers/${seg(customerId)}/unblock`,
    banned: () => `${V1}/moderation/banned`,
  },

  channels: {
    list: () => `${V1}/channels`,
    detail: (channelId: string) => `${V1}/channels/${seg(channelId)}`,
    /** Meta (WhatsApp/IG/Messenger) webhook callback URL + verify token to paste into Meta. */
    metaWebhookInfo: () => `${V1}/channels/meta/webhook-info`,
  },

  /** Analytics (admin; org-scoped, windowed by `from`/`to`). */
  analytics: {
    overview: () => `${V1}/analytics/overview`,
    leaderboard: () => `${V1}/analytics/leaderboard`,
    queue: () => `${V1}/analytics/queue`,
  },

  /** Attachment upload (agent) + public serve. */
  media: {
    upload: () => `${V1}/media/upload`,
  },

  /**
   * Public, unauthenticated widget endpoints (scoped by `publicKey` / `cid`).
   * ARCHITECTURE §7: `loadWidgetConfig` + create-session run here.
   */
  widgetPublic: {
    config: (cid: string) => `${V1}/public/widget/${seg(cid)}/config`,
    createSession: (cid: string) => `${V1}/public/widget/${seg(cid)}/sessions`,
    session: (cid: string, sessionId: string) =>
      `${V1}/public/widget/${seg(cid)}/sessions/${seg(sessionId)}`,
    messages: (cid: string, sessionId: string) =>
      `${V1}/public/widget/${seg(cid)}/sessions/${seg(sessionId)}/messages`,
    upload: (cid: string, sessionId: string) =>
      `${V1}/public/widget/${seg(cid)}/sessions/${seg(sessionId)}/upload`,
    rating: (cid: string, sessionId: string) =>
      `${V1}/public/widget/${seg(cid)}/sessions/${seg(sessionId)}/rating`,
    close: (cid: string, sessionId: string) =>
      `${V1}/public/widget/${seg(cid)}/sessions/${seg(sessionId)}/close`,
    voiceToken: (cid: string, sessionId: string) =>
      `${V1}/public/widget/${seg(cid)}/sessions/${seg(sessionId)}/voice-token`,
    consent: (cid: string) => `${V1}/public/widget/${seg(cid)}/consent`,
    /** Anonymous visitor presence beat (live-visitors panel). */
    visitorHeartbeat: (cid: string) => `${V1}/public/widget/${seg(cid)}/visitors/heartbeat`,
    /** Co-browse DOM frame (session-token scoped). */
    cobrowseFrame: (cid: string, sessionId: string) =>
      `${V1}/public/widget/${seg(cid)}/sessions/${seg(sessionId)}/cobrowse/frame`,
    offlineMessage: (cid: string) => `${V1}/public/widget/${seg(cid)}/offline-message`,
    queue: (cid: string, sessionId: string) =>
      `${V1}/public/widget/${seg(cid)}/sessions/${seg(sessionId)}/queue`,
    cobrowseStop: (cid: string, sessionId: string) =>
      `${V1}/public/widget/${seg(cid)}/sessions/${seg(sessionId)}/cobrowse/stop`,
  },
} as const;

export type Endpoints = typeof endpoints;
